import React, { useEffect, useRef, useState } from 'react';
import { InputText, Button, useDebounce } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { Agency } from 'types/aaaService';
import { toJS } from 'mobx';
import { createStyles } from './styles';

const AgencySearch = observer((): JSX.Element => {
  const classes = createStyles();

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce<string>(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mobx store
  const {
    roleTypeAndAgencyStore: { filteredAgencies, searchAgencyByName, setSelectedAgency, setFilteredAgencies },
  } = useRootStore();

  const convertedFilteredAgencies = filteredAgencies.map((agency) => toJS(agency)).slice();

  useEffect(() => {
    if (debouncedValue) {
      searchAgencyByName(debouncedValue);
    }
  }, [debouncedValue]);

  const resetSearchValue = (): void => {
    setFilteredAgencies([]);
    setSearchValue('');
  };

  const selectAgencyFromSearch = (agency: Agency): void => {
    setSelectedAgency(agency);
    resetSearchValue();
  };

  const handleClickOutside = (e: MouseEvent): void => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      resetSearchValue();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={classes.searchBlock} ref={containerRef}>
      <InputText
        name="searchAgency"
        label="Search agency"
        value={searchValue}
        description={
          <span className={classes.searchInputDescription}>{`Matches: ${convertedFilteredAgencies.length}`}</span>
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          setSearchValue(e.target.value);
        }}
        placeholder="Agency name"
        htmlType="search"
        className={classes.searchInput}
      />
      {convertedFilteredAgencies.length > 0 && (
        <ul className={classes.filterList}>
          {convertedFilteredAgencies.map((agency) => (
            <li
              key={agency.id}
              className={classes.filterItem}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(): void => {
                selectAgencyFromSearch(agency);
              }}
              onClick={(): void => {
                selectAgencyFromSearch(agency);
              }}
            >
              <span>{agency.name}</span>
              <Button
                type="text"
                color="primary"
                size="small"
                onClick={(): void => {
                  selectAgencyFromSearch(agency);
                }}
              >
                Select
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default AgencySearch;
